/**
 * Crypto helpers for the Revolut integration, each keyed off the secret that
 * matches its trust root:
 *  - refresh-token encryption at rest → dedicated `REVOLUT_TOKEN_KEY`.
 *  - OAuth-state signing → `BETTER_AUTH_SECRET`, which we already rely on for session signing.
 * Both derive a domain-separated subkey via labelled HMAC.
 */

import { createCipheriv, createDecipheriv, createHmac, randomBytes, timingSafeEqual } from 'node:crypto'

function encryptionSecret(): string {
  const key = process.env.REVOLUT_TOKEN_KEY
  if (!key) throw new Error('REVOLUT_TOKEN_KEY is not set.')
  return key
}

/** Mirrors better-auth's own dev fallback ([src/lib/auth.ts]) so state signing matches session signing. */
function stateSecret(): string {
  return process.env.BETTER_AUTH_SECRET || 'development-only-secret'
}

/** Domain-separated 32-byte key derived from the given root secret. */
function deriveKey(secret: string, label: string): Buffer {
  return createHmac('sha256', secret).update(label).digest()
}

// --- Refresh-token encryption at rest (AES-256-GCM) ---

const ENC_KEY_LABEL = 'revolut-refresh-token-enc-v1'

/** Encrypt a refresh token for storage. Format: `<iv>:<tag>:<ciphertext>` (base64url). */
export function encryptRefreshToken(plaintext: string): string {
  const iv = randomBytes(12)
  const cipher = createCipheriv('aes-256-gcm', deriveKey(encryptionSecret(), ENC_KEY_LABEL), iv)
  const ciphertext = Buffer.concat([cipher.update(plaintext, 'utf8'), cipher.final()])
  const tag = cipher.getAuthTag()
  return [iv.toString('base64url'), tag.toString('base64url'), ciphertext.toString('base64url')].join(':')
}

/** Decrypt a stored refresh token. Expects the `<iv>:<tag>:<ciphertext>` format written by `encryptRefreshToken`. */
export function decryptRefreshToken(stored: string): string {
  const parts = stored.split(':')
  if (parts.length !== 3) {
    throw new Error('Stored Revolut refresh token is not in the expected encrypted format.')
  }
  const [ivB64, tagB64, ctB64] = parts
  const decipher = createDecipheriv('aes-256-gcm', deriveKey(encryptionSecret(), ENC_KEY_LABEL), Buffer.from(ivB64, 'base64url'))
  decipher.setAuthTag(Buffer.from(tagB64, 'base64url'))
  return Buffer.concat([decipher.update(Buffer.from(ctB64, 'base64url')), decipher.final()]).toString('utf8')
}

// --- OAuth `state` (CSRF protection) ---

const STATE_KEY_LABEL = 'revolut-oauth-state-v1'
const STATE_TTL_MS = 10 * 60 * 1000

/**
 * Signed OAuth `state`, bound to the admin who started the connect flow. The
 * callback re-derives the HMAC and checks the embedded user id against the
 * authenticated session, so a forged or cross-user callback is rejected.
 */
export function signOAuthState(userId: number): string {
  const payload = Buffer.from(JSON.stringify({ uid: userId, n: randomBytes(8).toString('hex'), iat: Date.now() }), 'utf8').toString(
    'base64url'
  )
  const sig = createHmac('sha256', deriveKey(stateSecret(), STATE_KEY_LABEL)).update(payload).digest('base64url')
  return `${payload}.${sig}`
}

/** Verify a `state` returned to the callback: valid signature, matching user, not expired. */
export function verifyOAuthState(state: string | null, userId: number): boolean {
  if (!state) return false
  const [payload, sig] = state.split('.')
  if (!payload || !sig) return false
  const expected = createHmac('sha256', deriveKey(stateSecret(), STATE_KEY_LABEL)).update(payload).digest('base64url')
  const sigBuf = Buffer.from(sig)
  const expectedBuf = Buffer.from(expected)
  if (sigBuf.length !== expectedBuf.length || !timingSafeEqual(sigBuf, expectedBuf)) return false
  try {
    const { uid, iat } = JSON.parse(Buffer.from(payload, 'base64url').toString('utf8'))
    return uid === userId && typeof iat === 'number' && Date.now() - iat <= STATE_TTL_MS
  } catch {
    return false
  }
}
